import { useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Send } from 'lucide-react';

interface Props {
  disabled: boolean;
  onSend: (text: string) => void;
}

export function AssistantComposer({ disabled, onSend }: Props) {
  const [text, setText] = useState('');
  const taRef = useRef<HTMLTextAreaElement>(null);
  const trimmed = text.trim();
  const canSend = !disabled && trimmed.length > 0;
  const hasText = text.length > 0;

  // Auto-grow: a single line when empty, a 3-line floor while composing, then
  // growing with content up to a cap before it scrolls internally. The `rows`
  // attribute supplies the floor (browser-computed, no pixel guessing); the
  // measured scrollHeight drives growth beyond it.
  useLayoutEffect(() => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = ''; // fall back to the rows-derived height…
    const floor = el.clientHeight; // …which is our minimum (1 or 3 lines)
    el.style.height = 'auto';
    const max = 160;
    el.style.height = `${String(Math.min(Math.max(el.scrollHeight, floor), max))}px`;
  }, [text]);

  function submit() {
    if (!canSend) return;
    onSend(trimmed);
    setText('');
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  }

  return (
    <div className="border-t border-white/6 p-3">
      <div
        className={`flex items-end gap-2 bg-white/4 px-4 py-2 ${hasText ? 'rounded-2xl' : 'rounded-full'}`}
      >
        <textarea
          ref={taRef}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
          }}
          onKeyDown={onKeyDown}
          disabled={disabled}
          rows={hasText ? 3 : 1}
          placeholder="Ask about this client…"
          className="flex-1 resize-none overflow-y-auto bg-transparent text-[14px] leading-snug text-white/90 outline-none placeholder:text-white/40 disabled:opacity-60"
        />
        <button
          type="button"
          aria-label="Send"
          onClick={submit}
          disabled={!canSend}
          className="inline-flex h-8 w-8 flex-none items-center justify-center rounded-full bg-[#013125] text-white transition-colors enabled:hover:bg-[#024A38] disabled:opacity-40"
        >
          <Send className="h-4 w-4" aria-hidden="true" strokeWidth={1.75} />
        </button>
      </div>
    </div>
  );
}
